import { Injectable } from '@angular/core';
import { MatSnackBarRef, SimpleSnackBar } from '@angular/material/snack-bar';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { Observable, forkJoin, of } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { HttpErrorResponse } from '@angular/common/http';
import { NotificationService } from '../../../../common/services/notification/notification.service';
import { NfDetailsLoad, NfDetailsReset, NfDetailsUpdateStatus } from './nf-details.actions';
import { NfListService } from './nf-list.service';
import { NfVerificationResponse, NfStatusHistoryResponse, NfStatusUpdateRequest } from './models/nf-details.model';
import { NfStatus } from './models/nf-status.enum';

export interface NfDetailsStateModel {
  record: NfVerificationResponse | null;
  statusHistory: NfStatusHistoryResponse[];
  loading: boolean;
  submitting: boolean;
  actionsDisabled: boolean;
}

const initialState: NfDetailsStateModel = {
  record: null,
  statusHistory: [],
  loading: false,
  submitting: false,
  actionsDisabled: false,
};

@State<NfDetailsStateModel>({
  name: 'nfDetails',
  defaults: initialState,
})
@Injectable()
export class NfDetailsState {
  @Selector()
  static getRecord(state: NfDetailsStateModel): NfVerificationResponse | null {
    return state.record;
  }

  @Selector()
  static getStatusHistory(state: NfDetailsStateModel): NfStatusHistoryResponse[] {
    return state.statusHistory;
  }

  @Selector()
  static getLoading(state: NfDetailsStateModel): boolean {
    return state.loading;
  }

  @Selector()
  static getSubmitting(state: NfDetailsStateModel): boolean {
    return state.submitting;
  }

  @Selector()
  static getActionsDisabled(state: NfDetailsStateModel): boolean {
    return state.actionsDisabled;
  }

  constructor(private nfListService: NfListService, private notificationService: NotificationService) {}

  @Action(NfDetailsLoad)
  load(
    ctx: StateContext<NfDetailsStateModel>,
    { id }: NfDetailsLoad,
  ): Observable<void | MatSnackBarRef<SimpleSnackBar>> {
    ctx.patchState({ loading: true });

    return forkJoin([this.nfListService.getById(id), this.nfListService.getStatusHistory(id)]).pipe(
      switchMap(([record, statusHistory]: [NfVerificationResponse, NfStatusHistoryResponse[]]) => {
        const needsProcessing = record.status === NfStatus.Awaiting || record.status === NfStatus.Processing;

        if (!needsProcessing) {
          ctx.patchState({ record, statusHistory, loading: false });
          return of(undefined);
        }

        const payload: NfStatusUpdateRequest = { status: NfStatus.Processing };
        return this.nfListService.updateStatus(id, payload).pipe(
          switchMap((updated: NfVerificationResponse) =>
            this.nfListService.getStatusHistory(id).pipe(
              switchMap((updatedHistory: NfStatusHistoryResponse[]) => {
                ctx.patchState({ record: updated, statusHistory: updatedHistory, loading: false });
                return of(undefined);
              }),
            ),
          ),
          catchError(() => {
            ctx.patchState({ record, statusHistory, loading: false });
            return of(undefined);
          }),
        );
      }),
      catchError(() => {
        ctx.patchState({ loading: false });
        return of(this.notificationService.error('api.error.common'));
      }),
    );
  }

  @Action(NfDetailsReset)
  reset(ctx: StateContext<NfDetailsStateModel>): void {
    ctx.setState(initialState);
  }

  @Action(NfDetailsUpdateStatus)
  updateStatus(
    ctx: StateContext<NfDetailsStateModel>,
    { id, status }: NfDetailsUpdateStatus,
  ): Observable<void | MatSnackBarRef<SimpleSnackBar>> {
    ctx.patchState({ submitting: true });

    const payload: NfStatusUpdateRequest = { status };
    return this.nfListService.updateStatus(id, payload).pipe(
      switchMap((record: NfVerificationResponse) =>
        this.nfListService.getStatusHistory(id).pipe(
          switchMap((statusHistory: NfStatusHistoryResponse[]) => {
            ctx.patchState({ record, statusHistory, submitting: false });
            return of(undefined);
          }),
        ),
      ),
      catchError((err: HttpErrorResponse) => {
        ctx.patchState({ submitting: false });
        if (err.status === 409) {
          ctx.patchState({ actionsDisabled: true });
          return of(this.notificationService.error('notaFiscale.details.error.visitEnded'));
        }
        if (err.status === 422) {
          return of(this.notificationService.error('notaFiscale.details.error.invalidTransition'));
        }
        return of(this.notificationService.error('api.error.common'));
      }),
    );
  }
}
